import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Paperclip, Clock, CheckCircle2, Eye, MousePointerClick, AlertCircle, Download, Loader2 } from 'lucide-react';
import EmailStatusBadge from './EmailStatusBadge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { usePdfGeneration } from '@/hooks/usePdfGeneration';
import { fetchEstimateExportData } from '@/utils/estimateExportUtils';
import { fetchInvoiceExportData } from '@/utils/invoiceExportUtils';
import { toast } from 'sonner';

export interface EmailDetails {
  id: string;
  document_type: string;
  document_id?: string;
  version_id?: string | null;
  recipient_email: string;
  recipient_name?: string | null;
  subject: string;
  body_html: string | null;
  sent_at: string;
  status: string;
  delivered_at?: string | null;
  opened_at?: string | null;
  open_count?: number | null;
  clicked_at?: string | null;
  click_count?: number | null;
  bounced_at?: string | null;
  bounce_reason?: string | null;
  // Document context
  document_number?: string | null;
  version_number?: number | null;
  version_type?: string | null;
}

interface EmailDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: EmailDetails | null;
}

const EmailDetailModal: React.FC<EmailDetailModalProps> = ({
  open,
  onOpenChange,
  email,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { generatePdf } = usePdfGeneration();

  if (!email) return null;

  const getDocumentTypeLabel = () => {
    if (email.document_type === 'invoice') return 'Invoice';
    if (email.document_type === 'change_order') return 'Change Order';
    return 'Estimate';
  };

  const getVersionLabel = () => {
    if (!email.version_number) return null;
    if (email.version_type === 'change_order') {
      return `Change Order v${email.version_number}`;
    }
    return `Version ${email.version_number}`;
  };

  const getAttachmentLabel = () => {
    if (!email.version_number && !email.document_number) return `${getDocumentTypeLabel()} (PDF)`;
    if (email.version_number) {
      return `${getVersionLabel()} (PDF)`;
    }
    return `${getDocumentTypeLabel()} ${email.document_number} (PDF)`;
  };

  const handleDownloadAttachment = async () => {
    if (!email.document_id) {
      toast.error('Cannot download: document information not available');
      return;
    }

    setIsDownloading(true);
    try {
      if (email.document_type === 'invoice') {
        const exportData = await fetchInvoiceExportData(email.document_id);
        if (!exportData) throw new Error('Could not load invoice data');
        
        await generatePdf({
          htmlContent: '',
          estimateData: exportData,
          filename: `invoice-${exportData.invoice?.invoice_number || 'download'}.pdf`,
          type: 'invoice',
          action: 'download'
        });
      } else {
        const exportData = await fetchEstimateExportData(email.document_id, email.version_id || undefined);
        if (!exportData) throw new Error('Could not load estimate data');
        
        const versionSuffix = email.version_number ? `-v${email.version_number}` : '';
        await generatePdf({
          htmlContent: '',
          estimateData: exportData,
          filename: `estimate-${exportData.estimate?.estimate_number || 'download'}${versionSuffix}.pdf`,
          type: 'estimate',
          action: 'download'
        });
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline">{getDocumentTypeLabel()}</Badge>
            {email.version_number && (
              <Badge variant="secondary">
                {email.version_type === 'change_order' ? `CO v${email.version_number}` : `v${email.version_number}`}
              </Badge>
            )}
            <EmailStatusBadge
              status={email.status as any}
              sentAt={email.sent_at}
              deliveredAt={email.delivered_at}
              openedAt={email.opened_at}
              clickedAt={email.clicked_at}
              bouncedAt={email.bounced_at}
              openCount={email.open_count || 0}
              clickCount={email.click_count || 0}
              bounceReason={email.bounce_reason}
              showDetails
            />
          </div>
          <DialogTitle className="text-lg mt-2">{email.subject}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Email Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">To:</span>{' '}
              <span className="font-medium">
                {email.recipient_name ? `${email.recipient_name} <${email.recipient_email}>` : email.recipient_email}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Sent:</span>{' '}
              <span className="font-medium">{format(new Date(email.sent_at), 'dd MMMM yyyy HH:mm')}</span>
            </div>
          </div>

          <Separator />

          {/* Delivery Timeline */}
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Sent:</span>
              <span>{format(new Date(email.sent_at), 'dd MMMM yyyy HH:mm')}</span>
            </div>
            {email.delivered_at && (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                <span className="text-muted-foreground">Delivered:</span>
                <span>{format(new Date(email.delivered_at), 'dd MMMM yyyy HH:mm')}</span>
              </div>
            )}
            {email.opened_at && (
              <div className="flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-muted-foreground">Opened:</span>
                <span>{format(new Date(email.opened_at), 'dd MMMM yyyy HH:mm')}</span>
                {(email.open_count || 0) > 1 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {email.open_count}x
                  </Badge>
                )}
              </div>
            )}
            {email.clicked_at && (
              <div className="flex items-center gap-1.5">
                <MousePointerClick className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-muted-foreground">Clicked:</span>
                <span>{format(new Date(email.clicked_at), 'dd MMMM yyyy HH:mm')}</span>
                {(email.click_count || 0) > 1 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {email.click_count}x
                  </Badge>
                )}
              </div>
            )}
            {email.bounced_at && (
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-muted-foreground">Bounced:</span>
                <span>{format(new Date(email.bounced_at), 'dd MMMM yyyy HH:mm')}</span>
                {email.bounce_reason && (
                  <span className="text-destructive">({email.bounce_reason})</span>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Email Body */}
          <div className="flex-1 min-h-0">
            <ScrollArea className="h-[300px]">
              <div
                className="bg-background p-4 rounded-lg border prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: email.body_html || '<em>No content</em>' }}
              />
            </ScrollArea>
          </div>

          {/* Attachment */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{getAttachmentLabel()}</span>
            </div>
            {email.document_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAttachment}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1.5" />
                    Download
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDetailModal;
